import { ErrorOutlineRounded } from '@mui/icons-material';
import { Box, TextField, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';

import {
  Button,
  ModalContentContainer,
  SingleSelectDropdown,
} from '@/components';
import { SubscriptionCancelReasons } from '@/constants';
import { CancelStripeSubscriptionPayload } from '@/requests';

export type CancelStripeSubscriptionProps = {
  planValidUntil?: string;
  onConfirm: (args: CancelStripeSubscriptionPayload) => void;
  onCancel: () => void;
};

export const CancelStripeSubscription = ({
  planValidUntil,
  onConfirm,
  onCancel,
}: CancelStripeSubscriptionProps) => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [comment, setComment] = useState<string | null>(null);

  return (
    <Box sx={{ maxWidth: 600 }}>
      <ModalContentContainer stretch>
        <Typography variant="h2" sx={{ mb: 2 }}>
          Thinking about cancelling?
        </Typography>
        {!!planValidUntil && (
          <Typography variant="body2">
            Your subscription is active and will renew on{' '}
            <b>{format(new Date(planValidUntil), 'MMM d, yyyy')}</b>.
          </Typography>
        )}
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'grey50',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
          }}>
          <ErrorOutlineRounded
            sx={{ width: 30, height: 30, color: 'grey150' }}
          />
          <Typography
            variant="body3"
            sx={{
              color: 'text.main',
            }}>
            If you cancel today, you&apos;ll continue to have full access until
            the end of your current billing cycle.{' '}
            <b>
              Keep your current subscription active to lock in your preferred
              price going forward.
            </b>
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 3, mb: 1 }}>
          Why are you cancelling your subscription?{' '}
          <Typography component="span" sx={{ color: 'red' }}>
            *
          </Typography>
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <SingleSelectDropdown
            selectedOption={selectedReason ?? ''}
            options={SubscriptionCancelReasons.reduce(
              (acc, [key, label]) => {
                acc[key] = { text: label };
                return acc;
              },
              {} as Record<string, { text: string }>,
            )}
            onSave={setSelectedReason}
            title={selectedReason ? '' : 'Select one'}
            fullSize
          />
        </Box>

        {selectedReason === 'other' && (
          <TextField
            value={comment}
            onChange={e => setComment(e.target.value)}
            label="Please specify"
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            mt: 4,
          }}>
          <Button
            variant="secondary"
            label="Back"
            fullWidth={false}
            skinny={false}
            sx={{ width: '50%' }}
            onClick={onCancel}
          />
          <Button
            variant="failure"
            label="Cancel Subscription"
            fullWidth={false}
            skinny={false}
            disabled={!selectedReason}
            sx={{ width: '50%' }}
            onClick={() => onConfirm({ feedback: selectedReason, comment })}
          />
        </Box>
      </ModalContentContainer>
    </Box>
  );
};
